import { useCallback, useEffect, useRef, useState } from 'react';
import { triggerHaptic } from '@/pages/(app-webview)/utils/webview';

type PullState = 'idle' | 'pulling' | 'triggered' | 'refreshing';

const THRESHOLD = 64;
const DAMPEN_FACTOR = 0.45;
const MAX_PULL = 120;

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
  threshold?: number;
  disabled?: boolean;
}

interface UsePullToRefreshReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  pullDistance: number;
  state: PullState;
}

export function usePullToRefresh({
  onRefresh,
  threshold = THRESHOLD,
  disabled = false,
}: UsePullToRefreshOptions): UsePullToRefreshReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [state, setState] = useState<PullState>('idle');

  const startYRef = useRef(0);
  const canPullRef = useRef(false);
  const hapticFiredRef = useRef(false);
  const stateRef = useRef<PullState>('idle');
  const disabledRef = useRef(disabled);

  // refs를 동기화하여 이벤트 핸들러에서 최신 상태 참조
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  const reset = useCallback(() => {
    setPullDistance(0);
    setState('idle');
    hapticFiredRef.current = false;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    /** 터치 시작 지점에서 컨테이너까지 올라가며 scrollTop > 0인 스크롤 가능 요소가 있는지 확인 */
    const hasScrolledChild = (target: EventTarget | null): boolean => {
      let node = target as HTMLElement | null;
      while (node && node !== el) {
        if (node.scrollTop > 0 && node.scrollHeight > node.clientHeight) return true;
        node = node.parentElement;
      }
      return false;
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (disabledRef.current) return;
      if (stateRef.current === 'refreshing') return;

      // 터치 시작 시점에 내부 스크롤 영역이 위로 스크롤된 상태인지 판단
      canPullRef.current = el.scrollTop === 0 && !hasScrolledChild(e.target);
      startYRef.current = e.touches[0].clientY;
      hapticFiredRef.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (stateRef.current === 'refreshing') return;
      if (!canPullRef.current) return;

      // 스크롤 도중 scrollTop > 0이 되면 pull 비활성화
      if (el.scrollTop > 0) {
        canPullRef.current = false;
        if (stateRef.current !== 'idle') reset();
        return;
      }

      const currentY = e.touches[0].clientY;
      const rawDelta = currentY - startYRef.current;

      if (rawDelta <= 0) {
        if (stateRef.current !== 'idle') reset();
        // 위로 스크롤 — 기본 스크롤 동작에 맡김 (preventDefault 하지 않음)
        return;
      }

      // pulling 상태에서만 기본 동작(바운스) 방지
      e.preventDefault();

      const dampened = Math.min(rawDelta * DAMPEN_FACTOR, MAX_PULL);
      setPullDistance(dampened);

      if (dampened >= threshold) {
        if (!hapticFiredRef.current) {
          triggerHaptic();
          hapticFiredRef.current = true;
        }
        setState('triggered');
      } else {
        setState('pulling');
      }
    };

    const handleTouchEnd = async () => {
      if (stateRef.current === 'refreshing') return;

      if (stateRef.current === 'triggered') {
        setState('refreshing');
        setPullDistance(threshold); // refreshing 동안 고정 위치
        try {
          await onRefresh();
        } finally {
          reset();
        }
      } else {
        reset();
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [disabled, onRefresh, threshold, reset]);

  return { containerRef, pullDistance, state };
}
